import { useState } from "react";
import { Send, Stethoscope } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Card } from "@/components/Common";
import { PhotoRequests } from "@/components/analysis/IdentificationStep";

const NON_SO = "Non so";

export function Question({ q, value, onChange }) {
  const toggle = (opt) => {
    if (q.type === "single") return onChange(value === opt ? "" : opt);
    const cur = Array.isArray(value) ? value : [];
    onChange(cur.includes(opt) ? cur.filter((x) => x !== opt) : [...cur, opt]);
  };
  const on = (opt) => (q.type === "single" ? value === opt : Array.isArray(value) && value.includes(opt));
  return (
    <Card testid={`question-${q.id}`}>
      <p className="text-[18px] font-semibold leading-snug text-[#0D2517]">{q.text}</p>
      {q.why && <p className="mt-1 text-[16px] text-[#5b6b60]">{q.why}</p>}
      {q.type === "text" ? (
        <textarea className="field mt-3 min-h-[70px]" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={`answer-${q.id}`} placeholder="Scrivi qui (oppure lascia vuoto se non sai)" />
      ) : (
        <div className="mt-3 flex flex-wrap gap-2">
          {[...q.options, ...(q.type === "single" ? [NON_SO] : [])].map((opt, i) => (
            <button key={opt} type="button" onClick={() => toggle(opt)} className={`chip min-h-[44px] !text-base ${on(opt) ? "chip-on" : ""}`} data-testid={`answer-${q.id}-opt-${i}`}>{opt}</button>
          ))}
        </div>
      )}
    </Card>
  );
}

export default function AnamnesisStep({ analysis, reload }) {
  const round = analysis.rounds[analysis.rounds.length - 1];
  const [answers, setAnswers] = useState({});
  const [skipped, setSkipped] = useState([]);
  const [busy, setBusy] = useState(false);
  const openReqs = (analysis.photo_requests || []).filter((r) => r.status !== "skipped");
  const species = analysis.species || {};

  if (!round) return null;

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/analyses/${analysis.id}/answers`, { answers, skipped_requests: skipped });
      setAnswers({});
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const diagnoseNow = async () => {
    setBusy(true);
    await api.post(`/analyses/${analysis.id}/diagnose`, { answers, skipped_requests: skipped }).catch((e) => toast.error(errMsg(e)));
    setBusy(false);
    reload();
  };

  return (
    <div className="space-y-4 px-5 py-6" data-testid="anamnesis-step">
      {analysis.flow === "v3" ? (
        <div>
          <h1 className="font-serif text-[26px] font-semibold leading-tight text-[#0D2517]">{round.questions.length ? "Una domanda per essere più preciso" : "Serve un'altra foto"}</h1>
          <p className="mt-2 text-[17px] leading-relaxed text-[#2D3A30]">La risposta può cambiare la diagnosi o l'intervento. Se non lo sai, puoi procedere comunque.</p>
        </div>
      ) : (
      <div>
        <p className="eyebrow">Passo 4 · Domande di approfondimento · Round {round.n} di max 3</p>
        <h1 className="font-serif text-3xl font-semibold text-[#0D2517]">Qualche domanda sulla tua pianta</h1>
        <p className="mt-1 text-sm text-[#5b6b60]">{species.common_name} <span className="botanical">{species.botanical_name}</span> · domande scelte in base alle foto.</p>
      </div>
      )}

      {analysis.flow !== "v3" && round.symptoms_seen?.length > 0 && (
        <Card testid="symptoms-seen">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-[#5b6b60]">Cosa vedo nelle foto</p>
          <ul className="list-disc space-y-1 pl-5 text-sm">{round.symptoms_seen.map((s) => <li key={s}>{s}</li>)}</ul>
        </Card>
      )}

      {openReqs.length > 0 && (
        <PhotoRequests analysis={analysis} requests={openReqs} reload={reload} skipped={skipped} onSkip={(id) => setSkipped([...skipped, id])} />
      )}

      {round.questions.map((q) => (
        <Question key={q.id} q={q} value={answers[q.id]} onChange={(v) => setAnswers({ ...answers, [q.id]: v })} />
      ))}

      <button onClick={submit} disabled={busy} className="btn-primary min-h-[48px] w-full !text-base" data-testid="submit-answers-button">
        <Send size={16} /> INVIA RISPOSTE
      </button>
      <button onClick={diagnoseNow} disabled={busy} className="btn-secondary min-h-[48px] w-full bg-white !text-base" data-testid="diagnose-now-button">
        <Stethoscope size={16} /> Procedi con le informazioni attuali
      </button>
    </div>
  );
}
